import { Notification } from "@intentsify/types";
import { useQueryClient } from "@tanstack/react-query";
import { atom } from "jotai";
import { atomWithReset, useResetAtom } from "jotai/utils";
import uniqBy from "lodash/uniqBy";

export const unreadNotificationsState = atomWithReset<Notification[]>([]);

export const readNotificationsState = atomWithReset<Notification[] | undefined>(
  undefined
);

export const showReadState = atomWithReset<boolean>(false);

export const nextPageTokenState = atomWithReset<string | undefined>(undefined);

export const isFetchingUnreadState = atom<boolean>(false);

export const isFetchingUnreadNextPageState = atom<boolean>(false);

export const isFetchingSelector = atom((get) => {
  const isFetchingUnread = get(isFetchingUnreadState);
  const isFetchingUnreadNextPage = get(isFetchingUnreadNextPageState);

  return isFetchingUnread || isFetchingUnreadNextPage;
});

export const userNotificationsSelector = atom((get) => {
  const unread = get(unreadNotificationsState);
  const read = get(readNotificationsState);
  const showRead = get(showReadState);

  if (!showRead) {
    return unread;
  }

  const sorted = [...(unread ?? []), ...(read ?? [])].sort(
    (a, b) => new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf()
  );

  return uniqBy(sorted, (item) => item.id);
});

export const useResetOnClose = () => {
  const queryClient = useQueryClient();

  const resetShowRead = useResetAtom(showReadState);
  const resetNextPageToken = useResetAtom(nextPageTokenState);
  const resetReadNotifications = useResetAtom(readNotificationsState);

  return () => {
    resetShowRead();
    resetNextPageToken();
    resetReadNotifications();

    queryClient.resetQueries({
      queryKey: ["readNotifications"],
      exact: false,
    });
    queryClient.resetQueries({
      queryKey: ["unreadNotifications"],
      exact: false,
    });
  };
};

export const useResetOnClearAll = () => {
  const reset = useResetOnClose();
  const resetUnread = useResetAtom(unreadNotificationsState);

  return () => {
    reset();
    resetUnread();
  };
};
